
(() => {
  const root = document.querySelector('[data-crystal-finder]');
  if (!root) return;

  const form = root.querySelector('[data-chat-form]');
  const input = root.querySelector('[data-chat-input]');
  const conversation = root.querySelector('[data-conversation]');
  const suggestions = root.querySelector('[data-suggestions]');
  const submitButton = form?.querySelector('button[type="submit"]');
  const endpoint = root.dataset.endpoint;

  if (!form || !input || !conversation || !submitButton || !endpoint) {
    return;
  }

  const history = [];
  let busy = false;

  // Read Shopify's currently selected country and currency.
  // Values are read for every request so market changes are respected.
  const getShopifyMarket = () => {
    const country =
      root.dataset.country ||
      window.Shopify?.country ||
      '';

    const currency =
      root.dataset.currency ||
      window.Shopify?.currency?.active ||
      '';

    const validCode = (value) => {
      const code = String(value || '').trim().toUpperCase();
      return /^[A-Z]{2,3}$/.test(code) ? code : '';
    };

    return {
      country: validCode(country),
      currency: validCode(currency)
    };
  };

  const scrollToLatest = () => {
    requestAnimationFrame(() => {
      conversation.scrollTop = conversation.scrollHeight;
    });
  };

  const activateChat = () => {
    if (root.classList.contains('is-active')) return;

    root.classList.add('is-active');

    const welcome = root.querySelector('[data-welcome]');
    if (welcome) welcome.remove();

    scrollToLatest();
  };

  const addBubble = (role, content) => {
    const message = document.createElement('div');
    message.className = 'crystal-finder__message';

    const bubble = document.createElement('div');
    bubble.className =
      `crystal-finder__bubble crystal-finder__bubble--${role}`;

    bubble.textContent = String(content ?? '');

    message.appendChild(bubble);
    conversation.appendChild(message);

    scrollToLatest();

    return message;
  };

  const safeProductUrl = (value) => {
    try {
      const url = new URL(String(value), window.location.origin);

      if (
        url.protocol !== 'https:' &&
        url.protocol !== 'http:'
      ) {
        return null;
      }

      if (
        url.hostname !== window.location.hostname &&
        url.hostname !== 'yellowtreecompany.com' &&
        url.hostname !== 'www.yellowtreecompany.com'
      ) {
        return null;
      }

      return url.href;
    } catch {
      return null;
    }
  };

  const safeImageUrl = (value) => {
    try {
      const url = new URL(String(value), window.location.origin);

      return ['https:', 'http:'].includes(url.protocol)
        ? url.href
        : null;
    } catch {
      return null;
    }
  };

  // Create one product card using the existing design.
  const createProductCard = (product) => {
    if (!product || !product.title) return null;

    const card = document.createElement('article');
    card.className = 'crystal-finder__product';

    const imageUrl = product.image
      ? safeImageUrl(product.image)
      : null;

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'crystal-finder__product-image';
      image.src = imageUrl;
      image.alt = String(product.title);
      image.loading = 'lazy';

      card.appendChild(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'crystal-finder__product-image';
      placeholder.setAttribute('aria-hidden', 'true');

      card.appendChild(placeholder);
    }

    const copy = document.createElement('div');
    copy.className = 'crystal-finder__product-copy';

    const title = document.createElement('h2');
    title.className = 'crystal-finder__product-title';
    title.textContent = String(product.title);

    copy.appendChild(title);

    const footer = document.createElement('div');
    footer.className = 'crystal-finder__product-footer';

    if (product.price) {
      const price = document.createElement('span');
      price.className = 'crystal-finder__product-price';
      price.textContent = String(product.price);

      footer.appendChild(price);
    }

    const productUrl = product.url
      ? safeProductUrl(product.url)
      : null;

    if (productUrl) {
      const link = document.createElement('a');
      link.className = 'crystal-finder__product-link';
      link.href = productUrl;
      link.textContent = 'View product ↗';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      footer.appendChild(link);
    }

    copy.appendChild(footer);
    card.appendChild(copy);

    return card;
  };

  // Display up to TWO product recommendations.
  // Both use the existing compact product-card design.
  const addProduct = (products) => {
    if (!Array.isArray(products) || !products.length) return;

    const message = document.createElement('div');
    message.className = 'crystal-finder__message';

    const list = document.createElement('div');
    list.className = 'crystal-finder__products';

    // Avoid displaying duplicate product IDs.
    const seen = new Set();

    products.forEach((product) => {
      if (list.childElementCount >= 2) return;
      if (!product || !product.title) return;

      const identifier = String(
        product.id || product.url || product.title
      );

      if (seen.has(identifier)) return;
      seen.add(identifier);

      const card = createProductCard(product);

      if (card) {
        list.appendChild(card);
      }
    });

    // Do not insert an empty product container.
    if (!list.childElementCount) return;

    message.appendChild(list);
    conversation.appendChild(message);

    scrollToLatest();
  };

  const setSuggestions = (items) => {
    if (!suggestions || !Array.isArray(items)) return;

    // Suggestions stay hidden during active conversations.
    suggestions.replaceChildren();

    items.slice(0, 3).forEach((item) => {
      if (typeof item !== 'string' || !item.trim()) return;

      const button = document.createElement('button');
      button.className = 'crystal-finder__chip';
      button.type = 'button';
      button.dataset.prompt = item;
      button.textContent = item;

      suggestions.appendChild(button);
    });
  };

  const resizeInput = () => {
    input.style.height = 'auto';
    input.style.height =
      `${Math.min(input.scrollHeight, 115)}px`;
  };

  const setBusy = (value) => {
    busy = value;
    input.disabled = value;
    submitButton.disabled = value;
    root.classList.toggle('is-loading', value);
  };

  const submitMessage = async (value) => {
    const message = String(value ?? '').trim();

    if (!message || busy) return;

    activateChat();
    addBubble('user', message);

    const previousConversation = history.slice(-10);

    history.push({
      role: 'user',
      content: message
    });

    input.value = '';
    resizeInput();
    setBusy(true);

    const typing = document.createElement('div');
    typing.className = 'crystal-finder__typing';
    typing.textContent = 'Finding your best matches…';

    conversation.appendChild(typing);
    scrollToLatest();

    try {
      // Read the active Shopify market at request time.
      const market = getShopifyMarket();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          message,
          conversation: previousConversation,
          context: {
            page: 'crystal-finder',
            country: market.country,
            currency: market.currency
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Crystal Finder HTTP ${response.status}`);
      }

      const data = await response.json();

      typing.remove();

      const answer =
        typeof data.message === 'string' && data.message.trim()
          ? data.message.trim()
          : 'Tell me a little more about what you are looking for.';

      addBubble('assistant', answer);

      history.push({
        role: 'assistant',
        content: answer
      });

      // Render up to TWO returned Shopify products.
      addProduct(data.products);

      setSuggestions(data.suggestions);
    } catch (error) {
      typing.remove();

      addBubble(
        'assistant',
        'I could not connect just now. Please try again in a moment.'
      );
    } finally {
      setBusy(false);
      scrollToLatest();

      // Avoid opening the mobile keyboard automatically.
      if (window.matchMedia('(min-width: 750px)').matches) {
        input.focus({ preventScroll: true });
      }
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submitMessage(input.value);
  });

  input.addEventListener('input', resizeInput);

  input.addEventListener('keydown', (event) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.isComposing
    ) {
      event.preventDefault();
      submitMessage(input.value);
    }
  });

  if (suggestions) {
    suggestions.addEventListener('click', (event) => {
      const button = event.target.closest('[data-prompt]');

      if (button) {
        submitMessage(button.dataset.prompt);
      }
    });
  }

  resizeInput();
})();
